import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { LeaveService } from '@/services/Leave/LeaveSetting'
import ShiftAndTimeService from '@/services/ShiftAndTime/ShiftAndTime'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'
import { useAppSelector } from '@/store/store'
import type { ILeave, ILeaveResponse, ILeaveSetting } from '@/interface/leave'

const AddLeave: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const loggedInUser = useAppSelector((state) => state.user.user)
    const [isLoading, setIsLoading] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState<{ value: string; label: string }[]>([]);
    const [shifts, setShifts] = useState<{ value: string; label: string }[]>([]);

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<any>({
        defaultValues: {
            duration: "FULL_DAY",
            numberOfDays: 1,
        }
    });

    const startDate = watch("startDate");
    const endDate = watch("endDate");
    const duration = watch("duration");

    // Fetch leave types from settings to populate select
    const getLeaveTypes = async () => {
        try {
            const response = await LeaveService.getLeaveSettings();
            if (response?.status) {
                const settings = response?.data?.leaveSettings;
                const formatted = (settings || []).map((item: ILeaveSetting) => ({
                    value: item.type,
                    label: item.type
                }));
                if (formatted.length > 0) {
                    setLeaveTypes(formatted);
                } else {
                    setLeaveTypes([
                        { value: "Earned Leave", label: "Earned Leave" },
                        { value: "Casual Leave", label: "Casual Leave" },
                        { value: "Sick Leave", label: "Sick Leave" },
                    ]);
                }
            }
        } catch (error) {
            console.error("Error fetching leave types:", error);
            setLeaveTypes([
                { value: "Earned Leave", label: "Earned Leave" },
                { value: "Casual Leave", label: "Casual Leave" },
                { value: "Sick Leave", label: "Sick Leave" },
            ]);
        }
    }

    // Fetch shifts to populate select
    const getShifts = async () => {
        try {
            const response = await ShiftAndTimeService.getShiftAndTime();
            if (response?.status && response.data) {
                const formatted = (response.data.shift || []).map((item: any) => ({
                    value: item.id || "",
                    label: item.shiftName || ""
                }));
                setShifts(formatted);
            }
        } catch (error) {
            console.error("Error fetching shifts:", error);
        }
    }

    const getLeaveById = async () => {
        if (!id) return;
        try {
            const response = await LeaveService.getLeaveById(id);
            if (response?.status && response.data) {
                const data = response.data;
                setValue("leaveType", data.leaveType);
                setValue("startDate", data.startDate ? data.startDate.split('T')[0] : "");
                setValue("endDate", data.endDate ? data.endDate.split('T')[0] : "");
                setValue("duration", data.duration);
                setValue("numberOfDays", data.numberOfDays);
                setValue("reason", data.reason);
                setValue("shiftId", data.shiftId || "");
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    useEffect(() => {
        getLeaveTypes();
        getShifts();
        if (id) {
            getLeaveById();
        }
    }, [id]);

    // Auto-calculate numberOfDays based on dates and duration
    useEffect(() => {
        if (startDate && endDate) {
            if (duration === "FIRST_HALF" || duration === "SECOND_HALF") {
                setValue("numberOfDays", 0.5);
            } else {
                const start = new Date(startDate);
                const end = new Date(endDate);
                const diffTime = end.getTime() - start.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                setValue("numberOfDays", diffDays > 0 ? diffDays : 1);
            }
        }
    }, [startDate, endDate, duration]);

    // Ensure End Date is not prior to Start Date
    useEffect(() => {
        if (startDate && endDate) {
            if (new Date(endDate) < new Date(startDate)) {
                setValue("endDate", startDate);
            }
        }
    }, [startDate, endDate]);

    const onSubmit = async (data: ILeave) => {
        if (!loggedInUser?.id) {
            showToastNotification("danger", "User not logged in");
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                userId: loggedInUser.id,
                startDate: data.startDate,
                endDate: data.endDate,
                leaveType: data.leaveType,
                duration: data.duration,
                numberOfDays: Number(data.numberOfDays),
                reason: data.reason,
                shiftId: data.shiftId || undefined,
            }

            let response: ILeaveResponse;
            if (id) {
                // If update API isn't explicitly defined in image specs, we handle edit if route is called
                response = await LeaveService.updateLeave(payload, id); // Fallback to update call
            } else {
                response = await LeaveService.createLeave(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message || "Leave applied successfully")
                navigate(RoutesLink.myLeave)
            } else {
                showToastNotification("danger", response?.message || "Failed to submit leave")
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit Leave Application" : "Apply for Leave"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("leaveType", { ...errorTypes.required('Leave Type') })}
                                type="select"
                                label="Leave Type"
                                placeholder="Select Leave Type"
                                value={watch("leaveType")}
                                options={[
                                    { value: 'select', label: 'Select Leave Type' },
                                    ...leaveTypes
                                ]}
                                error={errors.leaveType?.message as string}
                            />
                            <FormField
                                {...register("shiftId", { ...errorTypes.required('Shift') })}
                                type="select"
                                label="Shift"
                                placeholder="Select Shift"
                                value={watch("shiftId")}
                                options={[
                                    { value: 'select', label: 'Select Shift' },
                                    ...shifts
                                ]}
                                error={errors.shiftId?.message as string}
                            />
                            <FormField
                                {...register("duration", { ...errorTypes.required('Duration') })}
                                type="select"
                                label="Duration"
                                value={watch("duration")}
                                options={[
                                    { value: 'select', label: 'Select Duration' },
                                    { value: 'FULL_DAY', label: 'Full Day' },
                                    { value: 'FIRST_HALF', label: 'First Half' },
                                    { value: 'SECOND_HALF', label: 'Second Half' },
                                ]}
                                error={errors.duration?.message as string}
                            />
                            <FormField
                                {...register("startDate", { ...errorTypes.required('Start Date') })}
                                type="date"
                                label="Start Date"
                                error={errors.startDate?.message as string}
                            />
                            <FormField
                                {...register("endDate", { ...errorTypes.required('End Date') })}
                                type="date"
                                label="End Date"
                                min={startDate}
                                error={errors.endDate?.message as string}
                            />
                            <FormField
                                {...register("numberOfDays", { ...errorTypes.required('Number of Days') })}
                                type="number"
                                step="0.5"
                                label="Number of Days"
                                placeholder="Auto-calculated"
                                error={errors.numberOfDays?.message as string}
                                readOnly
                            />
                            <FormField
                                {...register("reason", { ...errorTypes.required('Reason for Leave') })}
                                type="text"
                                label="Reason"
                                placeholder="Enter reason for leave"
                                error={errors.reason?.message as string}
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Submitting..." : (id ? "Update Leave" : "Apply Leave")}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddLeave